import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download,
  FileText,
  File,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from 'lucide-react';
import { Document } from '@/types/document';
import { formatFileSize } from '@/utils/formatters';
import {
  useDownloadUrl,
  useDownloadDocument,
} from '@/hooks/react-query/useDocuments';

interface DocumentViewerProps {
  document: Document | null;
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentViewer({
  document,
  isOpen,
  onClose,
}: DocumentViewerProps) {
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [prevDocumentId, setPrevDocumentId] = useState(document?.id);

  if (document?.id !== prevDocumentId) {
    setPrevDocumentId(document?.id);
    if (document?.id) {
      setZoom(100);
      setRotation(0);
    }
  }

  // Always use download-url endpoint (works for all storage types)
  const {
    data: downloadUrlResponse,
    isLoading,
    error: loadError,
  } = useDownloadUrl(document?.id, { enabled: isOpen && !!document });

  const downloadDocumentMutation = useDownloadDocument();

  // Fetch presigned URL and convert to blob for display (avoids CORS issues)
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const setupDocumentUrl = async () => {
      try {
        if (downloadUrlResponse?.download_url) {
          // Fetch the presigned URL as a blob
          const response = await fetch(downloadUrlResponse.download_url);
          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.statusText}`);
          }
          const blob = await response.blob();

          if (!cancelled) {
            objectUrl = URL.createObjectURL(blob);
            setDocumentUrl(objectUrl);
          }
          return;
        }

        // Clear URL if not available
        setDocumentUrl(null);
      } catch (error) {
        console.error('Failed to setup document URL:', error);
        setDocumentUrl(null);
      }
    };

    setupDocumentUrl();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [downloadUrlResponse]);

  // Handle escape key to close the viewer without bubbling
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    // Add listener with capture to intercept before bubbling
    window.document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, onClose]);

  const handleDownload = async () => {
    if (!document) return;

    await downloadDocumentMutation.mutateAsync({
      id: document.id,
      documentName: document.original_filename,
    });
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 25, 25));
  const handleRotate = () => setRotation((prev) => (prev + 90) % 360);
  const handleResetView = () => {
    setZoom(100);
    setRotation(0);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-red-600 mb-2">
              Failed to load document. Please try again.
            </p>
          </div>
        </div>
      );
    }

    if (!document || !documentUrl) return null;

    const mimeType = document.mime_type.toLowerCase();

    // Handle images
    if (mimeType.startsWith('image/')) {
      return (
        <div className="flex justify-center overflow-auto max-h-[70vh]">
          <img
            src={documentUrl}
            alt={document.original_filename}
            className="max-w-full h-auto object-contain transition-transform"
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transformOrigin: 'center',
            }}
          />
        </div>
      );
    }

    // Handle PDFs
    if (mimeType === 'application/pdf') {
      return (
        <div className="h-[70vh]">
          <iframe
            src={documentUrl}
            className="w-full h-full border-0"
            title={document.original_filename}
          />
        </div>
      );
    }

    // Handle text files
    if (
      mimeType.startsWith('text/') ||
      mimeType === 'application/json' ||
      mimeType === 'application/xml'
    ) {
      return (
        <div className="max-h-[70vh] overflow-auto">
          <iframe
            src={documentUrl}
            className="w-full h-96 border-0"
            title={document.original_filename}
          />
        </div>
      );
    }

    // Handle other document types
    if (
      mimeType === 'application/msword' ||
      mimeType ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      mimeType === 'application/vnd.ms-excel' ||
      mimeType ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      mimeType === 'application/vnd.ms-powerpoint' ||
      mimeType ===
        'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <File className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">
              This file type cannot be previewed in the browser.
            </p>
            <Button
              onClick={handleDownload}
              className="flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Download to View</span>
            </Button>
          </div>
        </div>
      );
    }

    // Default fallback
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <File className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            Preview not available for this file type.
          </p>
          <Button
            onClick={handleDownload}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>Download File</span>
          </Button>
        </div>
      </div>
    );
  };

  const showImageControls = document?.mime_type
    .toLowerCase()
    .startsWith('image/');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] overflow-hidden"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
          }
        }}
      >
        <DialogHeader className="flex flex-row items-center justify-between space-y-0 pb-4 pr-4">
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-lg font-semibold truncate">
              {document?.original_filename}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Document viewer showing {document?.original_filename}
            </DialogDescription>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <Badge variant="outline">
                {document?.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
              </Badge>
              <span>{document ? formatFileSize(document.file_size) : ''}</span>
              <span>
                Uploaded{' '}
                {document
                  ? new Date(document.upload_date).toLocaleDateString()
                  : ''}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            {showImageControls && documentUrl && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleZoomOut}
                  disabled={zoom <= 25}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600 min-w-12 text-center">
                  {zoom}%
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleZoomIn}
                  disabled={zoom >= 200}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleRotate}>
                  <RotateCw className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleResetView}>
                  Reset
                </Button>
              </>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={!documentUrl && !isLoading}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
}
